// @flow
import BluebirdPromise from 'bluebird';

import * as r from './resources';
import { jsonForResourceRequest, rangeString, post, del, put } from './http';

import {
  type SerializedCampaignDraft,
  type CampaignsQuery,
  type CampaignsQueryResponse,
  type CampaignDraft,
  type ContactsQuery,
  type ContactsQueryResponse,
  type GroupsQuery,
  type GroupsQueryResponse,
  type GroupResponse,
  type CampaignRecipientsQuery,
  type CampaignRecipientsQueryResponse,
  type VariationsQueryResponseForCampaigns,
  type VariationsQuery,
  type CampaignSendTestEmailQuery,
  type CampaignSendTestEmailResponse,
} from 'modules/campaign';
import type { Dispatch, GetState } from '~/store';

export const campaignsQuery =
  (
    apiUrl: string,
    query: CampaignsQuery,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<CampaignsQueryResponse>) =>
  (start: number, count: number): BluebirdPromise<CampaignsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.campaigns,
    });

export const campaignQuery = (
  apiUrl: string,
  id: string,
): BluebirdPromise<CampaignDraft> =>
  jsonForResourceRequest({
    apiUrl,
    resourceId: id,
    resource: r.campaign,
  });

export const campaignRecipientsQuery =
  (
    apiUrl: string,
    query: CampaignRecipientsQuery,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<CampaignRecipientsQueryResponse>) =>
  (
    start: number,
    count: number,
  ): BluebirdPromise<CampaignRecipientsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.campaignRecipients,
    });

export const createCampaign = (params: {
  campaign: SerializedCampaignDraft,
}): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  post(r.createCampaign, '', params);

export const updateCampaign = (
  { _id }: $ReadOnly<{ _id: string }>,
  updates: { campaign: SerializedCampaignDraft },
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  // eslint-disable-next-line no-underscore-dangle
  put(r.updateCampaign, _id, updates);

export const deleteCampaign = (
  id: string,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  del(r.campaign, id);

export const contactsQuery =
  (
    apiUrl: string,
    query: ContactsQuery,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<ContactsQueryResponse>) =>
  (start: number, count: number): BluebirdPromise<ContactsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.contacts,
    });

export const groupsQuery =
  (
    apiUrl: string,
    query: GroupsQuery,
  ): ((start: number, count: number) => BluebirdPromise<GroupsQueryResponse>) =>
  (start: number, count: number): BluebirdPromise<GroupsQueryResponse> =>
    jsonForResourceRequest({
      apiUrl,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
      resource: r.groups,
    });

export const getGroup = (
  id: string,
  apiUrl: string,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  // $FlowFixMe[prop-missing]
  jsonForResourceRequest({
    apiUrl,
    method: 'GET',
    resource: r.group,
    resourceId: (id: any),
  });

export const createGroup = (
  params: {
    group: $ReadOnly<{|
      name?: string,
      contact_ids: $ReadOnlyArray<string>,
      contact_selection?: $ReadOnly<{|
        contacts: $ReadOnly<{|
          keyword: string | null,
        |}>,
      |}>,
    |}>,
  },
  apiUrl: string,
): BluebirdPromise<GroupResponse> =>
  // $FlowFixMe[prop-missing]
  jsonForResourceRequest({
    apiUrl,
    method: 'POST',
    resource: r.group,
    params,
  });

export const updateGroup = (
  { id }: $ReadOnly<{ id: string }>,
  updates: {
    group: $ReadOnly<{|
      name?: string,
      contact_ids: $ReadOnlyArray<string>,
      contact_selection?: $ReadOnly<{|
        contacts: $ReadOnly<{|
          keyword: string | null,
        |}>,
      |}>,
    |}>,
  },
  apiUrl: string,
): ((dispatch: Dispatch, getState: GetState) => BluebirdPromise<mixed>) =>
  // $FlowFixMe[prop-missing]
  jsonForResourceRequest({
    apiUrl,
    method: 'PUT',
    resource: r.group,
    resourceId: (id: any),
    params: updates,
  });

export const deleteGroup = (
  { id }: $ReadOnly<{ id: string }>,
  apiUrl: string,
): BluebirdPromise<{ success: boolean }> =>
  // $FlowFixMe[prop-missing]
  jsonForResourceRequest({
    apiUrl,
    method: 'DELETE',
    resource: r.group,
    resourceId: (id: any),
  });

export const variationsForCampaignsQuery =
  (
    query: VariationsQuery,
    apiUrl: string,
  ): ((
    start: number,
    count: number,
  ) => BluebirdPromise<VariationsQueryResponseForCampaigns>) =>
  (
    start: number,
    count: number,
  ): BluebirdPromise<VariationsQueryResponseForCampaigns> =>
    jsonForResourceRequest({
      apiUrl,
      resource: r.variationsForCampaigns,
      resourceId: ({
        ...query,
        Range: rangeString({ start, count }),
      }: any),
    });

export const sendTestEmail = (
  query: CampaignSendTestEmailQuery,
  apiUrl: string,
): BluebirdPromise<CampaignSendTestEmailResponse> =>
  jsonForResourceRequest({
    apiUrl,
    method: 'POST',
    resource: r.testEmail,
    resourceId: ({
      ...query,
    }: any),
  });
